/**
 * Centralized API configuration for the UI
 *
 * This file manages all gateway/backend URLs for both HTTP and WebSocket connections.
 * All components should import from this file instead of using environment variables directly.
 */

/**
 * Determine the gateway HTTP URL based on environment
 * Priority: REACT_APP_GATEWAY_URL > derived from window.location > localhost
 */
const getGatewayHttpUrl = (): string => {
  // 1. Check environment variable first
  if (process.env.REACT_APP_GATEWAY_URL) {
    return process.env.REACT_APP_GATEWAY_URL;
  }

  // 2. In production, derive from current hostname
  if (process.env.NODE_ENV === 'production') {
    return `http://${window.location.hostname}:5001`;
  }

  // 3. Default to localhost for development
  return 'http://localhost:5001';
};

/**
 * Determine the gateway WebSocket URL based on environment
 * Priority: REACT_APP_GATEWAY_WS_URL > derived from HTTP URL
 */
const getGatewayWsUrl = (): string => {
  // 1. Check for WebSocket-specific environment variable
  if (process.env.REACT_APP_GATEWAY_WS_URL) {
    return process.env.REACT_APP_GATEWAY_WS_URL;
  }

  // 2. Derive from HTTP URL
  const httpUrl = getGatewayHttpUrl();
  const wsUrl = httpUrl.replace(/^http/, 'ws');

  // 3. Add /gateway suffix for WebSocket connection
  return `${wsUrl}/gateway`;
};

/**
 * Gateway HTTP URL for REST API calls
 * Example: http://localhost:5001
 */
export const GATEWAY_HTTP_URL = getGatewayHttpUrl();

/**
 * Gateway WebSocket URL for real-time communication
 * Example: ws://localhost:5001/gateway
 */
export const GATEWAY_WS_URL = getGatewayWsUrl();

/**
 * API endpoints
 */
export const API_ENDPOINTS = {
  // Sessions
  sessions: `${GATEWAY_HTTP_URL}/api/sessions`,
  apiSessions: `${GATEWAY_HTTP_URL}/api/api-sessions`,

  // Projects
  projects: `${GATEWAY_HTTP_URL}/api/projects`,

  // Automations
  automations: `${GATEWAY_HTTP_URL}/api/automations`,
} as const;

/**
 * Helper function to build API URL
 * @param path - API path (e.g., '/api/projects/123')
 * @returns Full URL
 */
export const buildApiUrl = (path: string): string => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${GATEWAY_HTTP_URL}${cleanPath}`;
};

// Log configuration on startup (development only)
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 [Config] API Configuration:', {
    GATEWAY_HTTP_URL,
    GATEWAY_WS_URL,
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_GATEWAY_URL: process.env.REACT_APP_GATEWAY_URL || '(not set)'
  });
}
